import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    Heart, ArrowLeft, ArrowRight, MapPin, Sparkle, Sun, House, ArrowsClockwise, Lightning,
    Timer, CalendarBlank, Suitcase, ClockCounterClockwise, MagnifyingGlass, CurrencyDollar,
    ForkKnife,
} from "@phosphor-icons/react";
import { travelDateApi } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { PassportLogo } from "@/components/Reveal";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

const CORAL = "#F4A28F";
const GOLD  = "#D4AF37";

// ----- Dynamic tile getters using t() -----
const getSettingTiles = (t) => [
    { key: "outdoor", label: t("travelDate.settingTiles.outdoor.label"), icon: Sun,   hint: t("travelDate.settingTiles.outdoor.hint") },
    { key: "indoor",  label: t("travelDate.settingTiles.indoor.label"),  icon: House, hint: t("travelDate.settingTiles.indoor.hint") },
    { key: "either",  label: t("travelDate.settingTiles.either.label"),  icon: ArrowsClockwise, hint: t("travelDate.settingTiles.either.hint") },
];

const getTimeTiles = (t) => [
    { key: "1h",       label: t("travelDate.timeTiles.1h.label"),       icon: Lightning, hint: t("travelDate.timeTiles.1h.hint") },
    { key: "half_day", label: t("travelDate.timeTiles.half_day.label"), icon: Sun,       hint: t("travelDate.timeTiles.half_day.hint") },
    { key: "full_day", label: t("travelDate.timeTiles.full_day.label"), icon: Timer,     hint: t("travelDate.timeTiles.full_day.hint") },
    { key: "weekend",  label: t("travelDate.timeTiles.weekend.label"),  icon: Suitcase,  hint: t("travelDate.timeTiles.weekend.hint") },
];

const getStyleTiles = (t) => [
    { key: "romantic",    label: t("travelDate.styleTiles.romantic.label"),    icon: "❤️", hint: t("travelDate.styleTiles.romantic.hint") },
    { key: "fun",         label: t("travelDate.styleTiles.fun.label"),         icon: "🎉", hint: t("travelDate.styleTiles.fun.hint") },
    { key: "adventurous", label: t("travelDate.styleTiles.adventurous.label"), icon: "🧗", hint: t("travelDate.styleTiles.adventurous.hint") },
    { key: "relaxing",    label: t("travelDate.styleTiles.relaxing.label"),    icon: "🧘", hint: t("travelDate.styleTiles.relaxing.hint") },
    { key: "luxury",      label: t("travelDate.styleTiles.luxury.label"),      icon: "💎", hint: t("travelDate.styleTiles.luxury.hint") },
    { key: "family",      label: t("travelDate.styleTiles.family.label"),      icon: "👨‍👩‍👧", hint: t("travelDate.styleTiles.family.hint") },
];

const getActivityTiles = (t) => [
    { key: "restaurant",     label: t("travelDate.activityTiles.restaurant"),     icon: "🍽" },
    { key: "coffee",         label: t("travelDate.activityTiles.coffee"),         icon: "☕" },
    { key: "park",           label: t("travelDate.activityTiles.park"),           icon: "🌳" },
    { key: "museum",         label: t("travelDate.activityTiles.museum"),         icon: "🏛" },
    { key: "viewpoint",      label: t("travelDate.activityTiles.viewpoint"),      icon: "🌅" },
    { key: "music",          label: t("travelDate.activityTiles.music"),          icon: "🎵" },
    { key: "beach",          label: t("travelDate.activityTiles.beach"),          icon: "🏖" },
    { key: "hike",           label: t("travelDate.activityTiles.hike"),           icon: "🥾" },
    { key: "shopping",       label: t("travelDate.activityTiles.shopping"),       icon: "🛍" },
    { key: "winery",         label: t("travelDate.activityTiles.winery"),         icon: "🍷" },
    { key: "sports",         label: t("travelDate.activityTiles.sports"),         icon: "🏟" },
    { key: "movie",          label: t("travelDate.activityTiles.movie"),          icon: "🎬" },
    { key: "spa",            label: t("travelDate.activityTiles.spa"),            icon: "💆" },
    { key: "art",            label: t("travelDate.activityTiles.art"),            icon: "🎨" },
    { key: "theater",        label: t("travelDate.activityTiles.theater"),        icon: "🎭" },
    { key: "concert",        label: t("travelDate.activityTiles.concert"),        icon: "🎤" },
    { key: "bowling",        label: t("travelDate.activityTiles.bowling"),        icon: "🎳" },
    { key: "arcade",         label: t("travelDate.activityTiles.arcade"),         icon: "🎮" },
    { key: "cooking_class",  label: t("travelDate.activityTiles.cooking_class"),  icon: "🧑‍🍳" },
    { key: "water_activity", label: t("travelDate.activityTiles.water_activity"), icon: "🚤" },
    { key: "horseback",      label: t("travelDate.activityTiles.horseback"),      icon: "🐎" },
    { key: "wellness",       label: t("travelDate.activityTiles.wellness"),       icon: "🧘" },
    { key: "local_events",   label: t("travelDate.activityTiles.local_events"),   icon: "📍" },
];

const getBudgetPresets = (t) => [
    { key: "free",   label: t("travelDate.targetFree"), value: 0    },
    { key: "25",     label: "$25",   value: 25   },
    { key: "50",     label: "$50",   value: 50   },
    { key: "100",    label: "$100",  value: 100  },
    { key: "150",    label: "$150",  value: 150  },
    { key: "250",    label: "$250",  value: 250  },
    { key: "500",    label: "$500+", value: 500  },
];

const SUGGEST_CITIES = ["Miami", "Kansas City", "Paris", "Kyoto", "New York", "Barcelona"];


export default function TravelDate() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const loc = useLocation();
    const { t } = useTranslation();

    const settingTiles = useMemo(() => getSettingTiles(t), [t]);
    const timeTiles = useMemo(() => getTimeTiles(t), [t]);
    const styleTiles = useMemo(() => getStyleTiles(t), [t]);
    const activityTiles = useMemo(() => getActivityTiles(t), [t]);
    const budgetPresets = useMemo(() => getBudgetPresets(t), [t]);

    // ---- Wizard state ----
    const [location, setLocation]       = useState("");
    const [coords, setCoords]           = useState(null);   // {lat, lon} when geolocation used
    const [budgetPreset, setBudgetPreset] = useState(null); // key of a preset
    const [budgetCustom, setBudgetCustom] = useState("");
    const [budgetMode, setBudgetMode]   = useState("total"); // total | detailed
    const [budgetBreakdown, setBudgetBreakdown] = useState({ food: "", activities: "", transportation: "", extras: "" });
    const [setting, setSetting]         = useState("either");
    const [timeAvailable, setTimeAvailable] = useState("half_day");
    const [customStart, setCustomStart] = useState("");
    const [customEnd,   setCustomEnd]   = useState("");
    const [customHours, setCustomHours] = useState("");
    const [dateStyle, setDateStyle]     = useState("romantic");
    const [secondaryStyle, setSecondaryStyle] = useState(null);
    const [activities, setActivities]   = useState([]);
    const [surpriseMe, setSurpriseMe]   = useState(false);
    const [notes, setNotes]             = useState("");

    // ---- UI state ----
    const [crafting, setCrafting] = useState(false);
    const [savedCount, setSavedCount] = useState(0);

    // ---- Discover results (shown below the wizard) ----
    const [discover, setDiscover]     = useState([]);
    const [discoverBusy, setDiscoverBusy] = useState(false);
    const [discoverError, setDiscoverError] = useState(false);
    const discoverKeyRef = useRef("");
    const [creatingFromCard, setCreatingFromCard] = useState(null);

    useEffect(() => {
        window.scrollTo(0, 0);
        if (loading) return;
        if (!user) { navigate("/auth"); return; }
        travelDateApi.list().then((d) => setSavedCount(d?.length || 0)).catch(() => {});
        const city = searchParams.get("city");
        if (city) setLocation(city);
        const editPrefs = loc.state?.editPrefs || ((typeof window !== "undefined") && window.history.state?.usr?.editPrefs);
        if (editPrefs) applyEditPrefs(editPrefs);
    }, [searchParams, user, loading, navigate, loc.state]);

    const applyEditPrefs = (p) => {
        if (!p) return;
        if (p.location)          setLocation(p.location);
        if (p.budget != null)    setBudgetCustom(String(p.budget));
        if (p.budget_breakdown)  {
            setBudgetMode("detailed");
            setBudgetBreakdown({
                food: p.budget_breakdown.food ?? "",
                activities: p.budget_breakdown.activities ?? "",
                transportation: p.budget_breakdown.transportation ?? "",
                extras: p.budget_breakdown.extras ?? "",
            });
        }
        if (p.setting)           setSetting(p.setting);
        if (p.time_available)    setTimeAvailable(p.time_available);
        if (p.date_style)        setDateStyle(p.date_style);
        if (p.secondary_style)   setSecondaryStyle(p.secondary_style);
        if (Array.isArray(p.preferred_activities)) setActivities(p.preferred_activities);
        if (p.surprise_me)       setSurpriseMe(true);
        if (p.notes)             setNotes(p.notes);
    };

    const effectiveBudget = useMemo(() => {
        if (budgetMode === "detailed") {
            const sum = Object.values(budgetBreakdown).reduce((a, v) => a + (parseFloat(v) || 0), 0);
            return sum > 0 ? sum : null;
        }
        if (budgetCustom.trim() !== "") return parseFloat(budgetCustom) || 0;
        if (budgetPreset !== null) {
            const p = budgetPresets.find(x => x.key === budgetPreset);
            return p ? p.value : null;
        }
        return null;
    }, [budgetMode, budgetBreakdown, budgetCustom, budgetPreset, budgetPresets]);

    // Debounced discover removed to save tokens. User must click explicitly.

    const runDiscover = async (loc, cat) => {
        if (!validate()) return;
        setDiscoverBusy(true); setDiscoverError(false);
        try {
            const res = await travelDateApi.search({
                location: loc, category: cat,
                budget: effectiveBudget || undefined,
            });
            setDiscover(res.results || []);
        } catch { setDiscoverError(true); setDiscover([]); }
        finally { setDiscoverBusy(false); }
    };

    const validate = () => {
        if (!location.trim()) { toast.error(t("travelDate.toasts.errNoDest", "Please pick a location first.")); return false; }
        if (effectiveBudget === null) { toast.error(t("travelDate.toasts.errNoBudget", "Please select a budget.")); return false; }
        if (!dateStyle) { toast.error(t("travelDate.toasts.errNoStyle", "Please select a vibe/style.")); return false; }
        return true;
    };

    const craftMyDate = async () => {
        if (!validate()) return;
        setCrafting(true);
        try {
            // Custom time handling — derive duration_hours from customHours or start/end
            let effTimeAvail = timeAvailable;
            let effDuration;
            if (timeAvailable === "custom") {
                effTimeAvail = undefined;  // don't send "custom" (backend has no such preset)
                if (customHours) {
                    effDuration = parseInt(customHours, 10);
                } else if (customStart && customEnd) {
                    // both HH:MM strings — compute delta in hours (assume same day)
                    const [sh, sm] = customStart.split(":").map(Number);
                    const [eh, em] = customEnd.split(":").map(Number);
                    let mins = (eh * 60 + em) - (sh * 60 + sm);
                    if (mins <= 0) mins += 24 * 60;
                    effDuration = Math.max(1, Math.round(mins / 60));
                } else {
                    effDuration = 3;
                }
            }

            const payload = {
                location: location.trim(),
                category: dateStyle,             // legacy field mapping
                date_style: dateStyle,
                secondary_style: secondaryStyle || undefined,
                setting,
                time_available: effTimeAvail,
                budget: effectiveBudget,
                budget_breakdown: budgetMode === "detailed"
                    ? Object.fromEntries(Object.entries(budgetBreakdown).map(([k, v]) => [k, parseFloat(v) || 0]).filter(([, v]) => v > 0))
                    : undefined,
                preferred_activities: activities.length ? activities : undefined,
                surprise_me: surpriseMe || activities.length === 0,   // default to surprise when no activities selected
                notes: notes.trim() || undefined,
                latitude: coords?.lat, longitude: coords?.lon,
                duration_hours: effDuration,
            };
            const created = await travelDateApi.create(payload);
            // Backend returns {id, status: "composing"} immediately — the detail page polls
            navigate(`/traveldate/${created.id}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("travelDate.toasts.errCompose"));
        } finally {
            setCrafting(false);
        }
    };

    const buildFromCard = async (r) => {
        setCreatingFromCard(r.id);
        try {
            const created = await travelDateApi.create({
                location: `${r.city}${r.country ? ", " + r.country : ""}`,
                category: r.category,
                date_style: dateStyle,
                setting,
                time_available: timeAvailable,
                budget: effectiveBudget || r.est_cost_usd || undefined,
                preferred_activities: activities.length ? activities : undefined,
                surprise_me: surpriseMe,
                notes: `Inspired by "${r.title}": ${r.why_recommended}`,
            });
            navigate(`/traveldate/${created.id}`);
        } catch (e) { toast.error(e?.response?.data?.detail || t("travelDate.toasts.errCreate")); }
        finally { setCreatingFromCard(null); }
    };

    if (loading || !user) return <div className="min-h-screen grid place-items-center text-white/60">{t("common.loading")}</div>;

    return (
        <div className="min-h-screen grain pb-32 md:pb-16" data-testid="traveldate-page">
            <Header savedCount={savedCount} />

            <div className="max-w-[1100px] mx-auto px-5 sm:px-8 md:px-10 pt-10 sm:pt-14">
                {/* Hero */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-4" style={{ background: "rgba(244,162,143,0.14)", border: `1px solid ${CORAL}55` }}>
                        <Heart size={11} weight="fill" color={CORAL} />
                        <span className="text-[10px] tracking-[0.3em] uppercase" style={{ color: CORAL }}>{t("travelDate.badge")}</span>
                    </div>
                    <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-white leading-[1.02] tracking-tight">
                        {t("travelDate.heroPart1")}<span style={{ fontStyle: "italic", color: CORAL }}>{t("travelDate.heroHighlight")}</span>{t("travelDate.heroPart2")}
                    </h1>
                    <p className="text-white/60 text-sm sm:text-base mt-3 max-w-xl">
                        {t("travelDate.heroSub")}
                    </p>
                </motion.div>

                {/* Section 1 — Location */}
                <SectionCard num={1} title={t("travelDate.steps.1")} icon={<MapPin size={14} weight="fill" color={CORAL} />}>
                    <LocationPicker
                        value={location}
                        onChange={(v, c) => { setLocation(v); if (c) setCoords(c); else setCoords(null); }}
                        onUseMyLocation={async () => {
                            if (!("geolocation" in navigator)) { toast.error(t("travelDate.toasts.errNoGeo")); return; }
                            navigator.geolocation.getCurrentPosition(async (pos) => {
                                try {
                                    const res = await travelDateApi.reverseGeocode(pos.coords.latitude, pos.coords.longitude);
                                    if (res?.display_name) {
                                        setLocation(res.display_name);
                                        setCoords({ lat: pos.coords.latitude, lon: pos.coords.longitude });
                                        toast.success(t("travelDate.toasts.succGeo"));
                                    } else { toast.error(t("travelDate.toasts.errGeoResolve")); }
                                } catch { toast.error(t("travelDate.toasts.errGeoResolve")); }
                            }, () => toast.error(t("travelDate.toasts.errGeoPerm")), { timeout: 8000 });
                        }}
                    />
                    <SuggestChips items={SUGGEST_CITIES} onPick={(c) => setLocation(c)} />
                </SectionCard>

                {/* Section 2 — Budget */}
                <SectionCard num={2} title={t("travelDate.steps.2")} icon={<CurrencyDollar size={14} weight="fill" color={CORAL} />}>
                    <div className="flex flex-wrap gap-2">
                        {budgetPresets.map((p) => (
                            <ChipButton
                                key={p.key}
                                active={budgetPreset === p.key && budgetCustom === ""}
                                onClick={() => { setBudgetPreset(p.key); setBudgetCustom(""); }}
                                data-testid={`td-budget-${p.key}`}
                            >
                                {p.label}
                            </ChipButton>
                        ))}
                        <input
                            value={budgetCustom}
                            onChange={(e) => { setBudgetCustom(e.target.value.replace(/[^0-9]/g, "")); setBudgetPreset(null); }}
                            placeholder={t("travelDate.customDollar")}
                            className="w-28 bg-transparent text-white text-sm px-4 py-1.5 rounded-full focus:outline-none placeholder:text-white/40"
                            style={{ background: "rgba(0,0,0,0.35)", border: `1px solid ${CORAL}33` }}
                            data-testid="td-budget-custom"
                        />
                    </div>
                    <details className="mt-4 border rounded-2xl p-4" style={{ borderColor: `rgba(212,175,55,0.3)` }}>
                        <summary className="cursor-pointer font-serif text-white outline-none select-none flex items-center justify-between" onClick={() => setBudgetMode(budgetMode === "detailed" ? "total" : "detailed")}>
                            Advanced Budgeting
                        </summary>
                        <AnimatePresence initial={false}>
                            {budgetMode === "detailed" && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                                    className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 overflow-hidden"
                                >
                                    {[
                                        ["food",           t("travelDate.breakdown.food")],
                                        ["activities",     t("travelDate.breakdown.activities")],
                                        ["transportation", t("travelDate.breakdown.transportation")],
                                        ["extras",         t("travelDate.breakdown.extras")],
                                    ].map(([k, label]) => (
                                        <label key={k} className="rounded-2xl p-3" style={{ background: "rgba(0,0,0,0.35)", border: `1px solid ${CORAL}22` }}>
                                            <div className="text-[10px] tracking-[0.15em] uppercase text-white/50">{label}</div>
                                            <div className="flex items-center gap-1 mt-1">
                                                <span className="text-white/70 text-xs">$</span>
                                                <input
                                                    inputMode="numeric"
                                                    value={budgetBreakdown[k]}
                                                    onChange={(e) => setBudgetBreakdown({ ...budgetBreakdown, [k]: e.target.value.replace(/[^0-9]/g, "") })}
                                                    placeholder="0"
                                                    className="w-full bg-transparent text-white text-sm focus:outline-none placeholder:text-white/40"
                                                    data-testid={`td-budget-${k}`}
                                                />
                                            </div>
                                        </label>
                                    ))}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </details>
                    {effectiveBudget !== null && (
                        <div className="mt-3 text-[11px] tracking-[0.2em] uppercase text-white/50">
                            {t("travelDate.targetBudget", { budget: effectiveBudget || t("travelDate.targetFree") })}
                        </div>
                    )}
                </SectionCard>

                {/* Section 3 — Setting */}
                <SectionCard num={3} title={t("travelDate.steps.3")} icon={<Sun size={14} weight="fill" color={CORAL} />}>
                    <TileGrid cols={3}>
                        {settingTiles.map((tTile) => (
                            <Tile
                                key={tTile.key} active={setting === tTile.key} onClick={() => setSetting(tTile.key)}
                                icon={<tTile.icon size={22} color={setting === tTile.key ? CORAL : "white"} />} label={tTile.label} hint={tTile.hint}
                                testId={`td-setting-${tTile.key}`}
                            />
                        ))}
                    </TileGrid>
                </SectionCard>

                {/* Section 4 — Time Available */}
                <SectionCard num={4} title={t("travelDate.steps.4")} icon={<Timer size={14} weight="fill" color={CORAL} />}>
                    <TileGrid cols={4}>
                        {timeTiles.map((tTile) => (
                            <Tile
                                key={tTile.key} active={timeAvailable === tTile.key} onClick={() => setTimeAvailable(tTile.key)}
                                icon={<tTile.icon size={22} color={timeAvailable === tTile.key ? CORAL : "white"} />} label={tTile.label} hint={tTile.hint}
                                testId={`td-time-${tTile.key}`}
                            />
                        ))}
                        <Tile
                            active={timeAvailable === "custom"} onClick={() => setTimeAvailable("custom")}
                            icon={<CalendarBlank size={22} color={timeAvailable === "custom" ? CORAL : "white"} />}
                            label={t("travelDate.timeTiles.custom.label")} hint={t("travelDate.timeTiles.custom.hint")} testId="td-time-custom"
                        />
                    </TileGrid>
                    <AnimatePresence>
                        {timeAvailable === "custom" && (
                            <motion.div
                                initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                                className="mt-3 grid grid-cols-2 gap-2"
                            >
                                <TimeField label={t("travelDate.customTime.start")}   value={customStart} onChange={setCustomStart} testId="td-time-start" type="time" />
                                <TimeField label={t("travelDate.customTime.end")}     value={customEnd}   onChange={setCustomEnd}   testId="td-time-end" type="time" />
                            </motion.div>
                        )}
                    </AnimatePresence>
                </SectionCard>

                {/* Section 5 — Date Style */}
                <SectionCard num={5} title={t("travelDate.steps.5")} icon={<Heart size={14} weight="fill" color={CORAL} />}>
                    <TileGrid cols={3}>
                        {styleTiles.map((tTile) => (
                            <Tile
                                key={tTile.key} active={dateStyle === tTile.key} onClick={() => setDateStyle(tTile.key)}
                                icon={<span className="text-xl leading-none">{tTile.icon}</span>} label={tTile.label} hint={tTile.hint}
                                testId={`td-style-${tTile.key}`}
                            />
                        ))}
                    </TileGrid>
                    <div className="mt-3 text-[11px] tracking-[0.2em] uppercase text-white/50 mb-1.5">{t("travelDate.addSecondaryStyle")}</div>
                    <div className="flex flex-wrap gap-1.5">
                        {styleTiles.filter(tTile => tTile.key !== dateStyle).map((tTile) => (
                            <ChipButton
                                key={tTile.key}
                                active={secondaryStyle === tTile.key}
                                onClick={() => setSecondaryStyle(secondaryStyle === tTile.key ? null : tTile.key)}
                                data-testid={`td-secondary-${tTile.key}`}
                            >
                                <span className="mr-1">{tTile.icon}</span>{tTile.label}
                            </ChipButton>
                        ))}
                    </div>
                </SectionCard>

                {/* Section 6 — Preferred Activities */}
                <SectionCard num={6} title={t("travelDate.steps.6")} icon={<ForkKnife size={14} weight="fill" color={CORAL} />}>
                    <div className="flex flex-wrap gap-1.5">
                        {activityTiles.map((a) => {
                            const active = activities.includes(a.key);
                            return (
                                <ChipButton
                                    key={a.key} active={active}
                                    onClick={() => setActivities(active ? activities.filter(x => x !== a.key) : [...activities, a.key])}
                                    data-testid={`td-activity-${a.key}`}
                                >
                                    <span className="mr-1">{a.icon}</span>{a.label}
                                </ChipButton>
                            );
                        })}
                    </div>
                    <label className="mt-4 inline-flex items-center gap-2 text-sm text-white/80 cursor-pointer select-none" data-testid="td-surprise">
                        <input type="checkbox" checked={surpriseMe} onChange={(e) => setSurpriseMe(e.target.checked)} className="accent-[#F4A28F]" />
                        <Sparkle size={12} weight="fill" color={GOLD} /> {t("travelDate.surpriseMe")}
                    </label>
                    <div className="mt-2">
                        <input
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder={t("travelDate.notesPlaceholder")}
                            className="w-full bg-transparent text-white text-sm px-4 py-2.5 rounded-2xl focus:outline-none placeholder:text-white/40"
                            style={{ background: "rgba(0,0,0,0.35)", border: `1px solid ${CORAL}22` }}
                            maxLength={280}
                            data-testid="td-notes"
                        />
                    </div>
                </SectionCard>

                {/* CTA (inline for desktop) */}
                <div className="hidden md:flex justify-end mt-8">
                    <CraftButton onClick={craftMyDate} busy={crafting} />
                </div>

                {/* Discover cards beneath the planner */}
                <div className="mt-14">
                    <div className="flex items-center justify-between mb-4">
                        <div className="overline" style={{ color: CORAL }}>
                            {location.trim().length >= 2 ? t("travelDate.ideasAround", { location }) : "TravelDate™ AI Ideas"}
                        </div>
                        <button onClick={() => runDiscover(location, dateStyle)} disabled={discoverBusy} className="px-4 py-1.5 rounded-full text-xs transition-colors hover:bg-white/5 border" style={{ borderColor: CORAL, color: CORAL }}>
                            {discoverBusy ? "Generating..." : "Generate AI Ideas"}
                        </button>
                    </div>
                    {discoverBusy && <div className="text-white/50 text-sm">{t("travelDate.composingOptions")}</div>}
                    {!discoverBusy && discover.length > 0 && (
                        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                            {discover.map((r, i) => (
                                <DateCard key={r.id} r={r} onBuild={() => buildFromCard(r)} busy={creatingFromCard === r.id} idx={i} />
                            ))}
                        </div>
                    )}
                    {!discoverBusy && discover.length === 0 && !discoverError && location.trim().length < 2 && (
                        <div className="grid gap-4 sm:grid-cols-3">
                            {[
                                [t("travelDate.featureCard1.title"), t("travelDate.featureCard1.sub")],
                                [t("travelDate.featureCard2.title"), t("travelDate.featureCard2.sub")],
                                [t("travelDate.featureCard3.title"), t("travelDate.featureCard3.sub")],
                            ].map(([titleText, subText], k) => (
                                <div key={k} className="rounded-2xl p-5" style={{ background: "var(--navy-deep)", border: "1px solid rgba(255,255,255,0.06)" }}>
                                    <Heart size={16} weight="fill" color={CORAL} />
                                    <div className="font-serif text-lg text-white mt-2">{titleText}</div>
                                    <div className="text-white/60 text-[12px] mt-1">{subText}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Sticky mobile CTA */}
            <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 px-4 pb-4 pt-3" style={{ background: "linear-gradient(180deg, rgba(11,19,32,0) 0%, rgba(11,19,32,0.94) 45%, rgba(11,19,32,1) 100%)" }}>
                <CraftButton onClick={craftMyDate} busy={crafting} full testId="td-craft-my-date-mobile" />
            </div>

            {/* Full-screen crafting overlay */}
            <AnimatePresence>{crafting && <CraftingOverlay />}</AnimatePresence>
        </div>
    );
}


// ==================================================================
// Header
// ==================================================================
function Header({ savedCount }) {
    const { t } = useTranslation();
    return (
        <header className="sticky top-0 z-40 glass border-b" style={{ borderColor: "rgba(244,162,143,0.15)" }}>
            <div className="max-w-[1200px] mx-auto px-5 sm:px-8 md:px-10 py-4 flex items-center justify-between gap-3">
                <Link to="/app" className="flex items-center gap-3">
                    <ArrowLeft size={16} color={CORAL} />
                    <PassportLogo size={26} />
                    <div className="hidden sm:block leading-none">
                        <div className="font-serif text-base text-white">{t("conciergeApp.passport")}</div>
                        <div className="text-[9px] tracking-[0.3em] uppercase" style={{ color: CORAL }}>TravelDate™</div>
                    </div>
                </Link>
                <div className="flex items-center gap-2">
                    <Link to="/traveldate/history" className="btn-ghost text-xs !py-2 !px-3 relative" data-testid="traveldate-history-link">
                        <ClockCounterClockwise size={12} /> {t("travelDate.myTravelDates")}
                        {savedCount > 0 && <span className="ml-1 text-[9px] px-1.5 rounded-full" style={{ background: "rgba(244,162,143,0.2)", color: CORAL }}>{savedCount}</span>}
                    </Link>
                </div>
            </div>
        </header>
    );
}


// ==================================================================
// Section card
// ==================================================================
function SectionCard({ num, title, icon, children }) {
    const { t } = useTranslation();
    return (
        <motion.section
            initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }}
            className="mt-8 rounded-3xl p-5 sm:p-7"
            style={{ background: "var(--navy-deep)", border: `1px solid ${CORAL}22` }}
            data-testid={`td-section-${num}`}
        >
            <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-full grid place-items-center text-[11px] font-semibold" style={{ background: "rgba(244,162,143,0.12)", color: CORAL, border: `1px solid ${CORAL}44` }}>
                    {num}
                </div>
                <div>
                    <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 flex items-center gap-1.5">{icon} {t("travelDate.stepNum", { num })}</div>
                    <div className="font-serif text-2xl text-white leading-none mt-1">{title}</div>
                </div>
            </div>
            {children}
        </motion.section>
    );
}


// ==================================================================
// Reusable UI atoms
// ==================================================================
function ChipButton({ active, onClick, children, ...rest }) {
    return (
        <button
            type="button" onClick={onClick}
            className="text-xs px-3 py-1.5 rounded-full inline-flex items-center transition-all"
            style={{
                background: active ? "rgba(244,162,143,0.18)" : "rgba(255,255,255,0.04)",
                border: `1px solid ${active ? CORAL + "88" : "rgba(255,255,255,0.08)"}`,
                color: active ? CORAL : "rgba(255,255,255,0.85)",
            }}
            {...rest}
        >
            {children}
        </button>
    );
}

function ToggleTab({ active, onClick, children, ...rest }) {
    return (
        <button
            type="button" onClick={onClick}
            className="text-[11px] tracking-[0.15em] uppercase px-3 py-1.5 rounded-full transition-all"
            style={{
                background: active ? "rgba(244,162,143,0.14)" : "transparent",
                border: `1px solid ${active ? CORAL + "77" : "rgba(255,255,255,0.1)"}`,
                color: active ? CORAL : "rgba(255,255,255,0.6)",
            }}
            {...rest}
        >
            {children}
        </button>
    );
}

function TileGrid({ cols = 3, children }) {
    const cls = cols === 4 ? "grid-cols-2 sm:grid-cols-3 md:grid-cols-5" : "grid-cols-2 sm:grid-cols-3";
    return <div className={`grid ${cls} gap-2`}>{children}</div>;
}

function Tile({ active, onClick, icon, label, hint, testId }) {
    return (
        <button
            type="button" onClick={onClick} data-testid={testId}
            className="rounded-2xl p-3 text-left transition-all"
            style={{
                background: active ? "rgba(244,162,143,0.14)" : "rgba(255,255,255,0.03)",
                border: `1px solid ${active ? CORAL + "88" : "rgba(255,255,255,0.08)"}`,
                boxShadow: active ? `0 0 0 4px rgba(244,162,143,0.08)` : "none",
                minHeight: 72,
            }}
        >
            <div className="flex items-center justify-between">
                <div>{icon}</div>
                {active && <Heart size={11} weight="fill" color={CORAL} />}
            </div>
            <div className="mt-1.5 text-white text-sm font-medium leading-none">{label}</div>
            {hint && <div className="text-[10px] mt-1" style={{ color: active ? CORAL : "rgba(255,255,255,0.5)" }}>{hint}</div>}
        </button>
    );
}

function TimeField({ label, value, onChange, testId, type = "text", placeholder }) {
    return (
        <label className="rounded-2xl p-3" style={{ background: "rgba(0,0,0,0.35)", border: `1px solid ${CORAL}22` }}>
            <div className="text-[10px] tracking-[0.15em] uppercase text-white/50">{label}</div>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="w-full bg-transparent text-white text-sm mt-1 focus:outline-none placeholder:text-white/30"
                data-testid={testId}
            />
        </label>
    );
}


// ==================================================================
// Location picker with debounced autocomplete
// ==================================================================
function LocationPicker({ value, onChange, onUseMyLocation }) {
    const { t } = useTranslation();
    const [q, setQ] = useState(value || "");
    const [suggestions, setSuggestions] = useState([]);
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const inputRef = useRef(null);
    const wrapRef = useRef(null);
    const abortRef = useRef(null);

    useEffect(() => { setQ(value || ""); }, [value]);

    useEffect(() => {
        if (!q || q.length < 2) { setSuggestions([]); return; }
        setBusy(true);
        const tTimer = setTimeout(async () => {
            try {
                if (abortRef.current) abortRef.current.aborted = true;
                const ctl = { aborted: false };
                abortRef.current = ctl;
                const res = await travelDateApi.geocode(q);
                if (!ctl.aborted) setSuggestions((res.results || []).slice(0, 6));
            } catch { /* ignore */ }
            finally { setBusy(false); }
        }, 260);
        return () => clearTimeout(tTimer);
    }, [q]);

    // Close on Escape + click outside
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
        window.addEventListener("keydown", onKey);
        document.addEventListener("mousedown", onDown);
        return () => { window.removeEventListener("keydown", onKey); document.removeEventListener("mousedown", onDown); };
    }, [open]);

    const pick = (s) => {
        onChange(s.primary + (s.secondary ? `, ${s.secondary}` : ""), { lat: s.latitude, lon: s.longitude });
        setOpen(false);
    };

    return (
        <div className="relative" ref={wrapRef}>
            <div className="flex items-center gap-2 rounded-full px-4 py-3" style={{ background: "rgba(0,0,0,0.35)", border: `1px solid ${CORAL}33` }}>
                <MagnifyingGlass size={14} color={CORAL} />
                <input
                    ref={inputRef}
                    value={q}
                    onChange={(e) => { setQ(e.target.value); onChange(e.target.value, null); setOpen(true); }}
                    onFocus={() => setOpen(true)}
                    placeholder="Paris, France · or 'downtown'"
                    className="flex-1 bg-transparent text-white text-sm focus:outline-none placeholder:text-white/40"
                    data-testid="td-location-input"
                    autoComplete="off"
                />
                <button
                    type="button" onClick={onUseMyLocation}
                    className="text-[11px] tracking-[0.15em] uppercase px-3 py-1.5 rounded-full shrink-0"
                    style={{ background: "rgba(244,162,143,0.14)", border: `1px solid ${CORAL}66`, color: CORAL }}
                    data-testid="td-use-my-location"
                >
                    <MapPin size={11} weight="fill" className="inline mr-1" /> {t("travelDate.useMyLocation")}
                </button>
            </div>
            <AnimatePresence>
                {open && suggestions.length > 0 && (
                    <motion.ul
                        initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
                        className="absolute z-30 left-0 right-0 mt-1 rounded-2xl overflow-hidden"
                        style={{ background: "var(--navy)", border: `1px solid ${CORAL}44`, boxShadow: "0 10px 30px rgba(0,0,0,0.35)" }}
                        data-testid="td-location-suggestions"
                    >
                        {suggestions.map((s) => (
                            <li key={s.id} onMouseDown={(e) => { e.preventDefault(); pick(s); }} className="px-4 py-2.5 hover:bg-white/5 cursor-pointer flex items-start gap-2" data-testid="td-location-suggest-item">
                                <MapPin size={12} weight="fill" color={CORAL} className="mt-1 shrink-0" />
                                <div className="min-w-0">
                                    <div className="text-white text-sm truncate">{s.primary}</div>
                                    {s.secondary && <div className="text-white/50 text-[11px] truncate">{s.secondary}</div>}
                                </div>
                            </li>
                        ))}
                    </motion.ul>
                )}
            </AnimatePresence>
            {busy && <div className="absolute right-4 top-3 text-[10px] text-white/40">{t("common.searching")}</div>}
        </div>
    );
}

function SuggestChips({ items, onPick }) {
    const { t } = useTranslation();
    return (
        <div className="flex flex-wrap gap-1.5 mt-3">
            <span className="text-[10px] tracking-[0.2em] uppercase text-white/40 mr-1 mt-1">{t("travelDate.try")}</span>
            {items.map((c) => (
                <button key={c} onClick={() => onPick(c)} className="text-[11px] px-2.5 py-1 rounded-full transition-colors" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.7)" }} data-testid={`td-suggest-${c}`}>{c}</button>
            ))}
        </div>
    );
}


// ==================================================================
// Craft button
// ==================================================================
function CraftButton({ onClick, busy, full = false, testId = "td-craft-my-date" }) {
    const { t } = useTranslation();
    return (
        <motion.button
            type="button" onClick={onClick} disabled={busy}
            whileTap={{ scale: 0.97 }}
            className={`rounded-full font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60 ${full ? "w-full py-4 text-base" : "px-8 py-3 text-sm"}`}
            style={{
                background: `linear-gradient(90deg, ${CORAL} 0%, #F4C6A5 100%)`, color: "var(--navy)",
                boxShadow: "0 8px 24px rgba(244,162,143,0.35)",
            }}
            data-testid={testId}
        >
            <Heart size={14} weight="fill" /> {busy ? t("travelDate.craftingBtn") : t("travelDate.craftMyDate")} <ArrowRight size={12} weight="bold" />
        </motion.button>
    );
}


// ==================================================================
// Crafting overlay
// ==================================================================
function CraftingOverlay() {
    const { t } = useTranslation();
    const craftingLines = t("travelDate.craftingOverlay.lines", { returnObjects: true }) || [];
    const [i, setI] = useState(0);
    useEffect(() => {
        if (!craftingLines.length) return;
        const timer = setInterval(() => setI((v) => (v + 1) % craftingLines.length), 1400);
        return () => clearInterval(timer);
    }, [craftingLines.length]);
    return (
        <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[90] grid place-items-center px-6"
            style={{ background: "rgba(5,10,17,0.9)", backdropFilter: "blur(12px)" }}
            data-testid="td-crafting-overlay"
        >
            <div className="text-center">
                <motion.div
                    animate={{ scale: [1, 1.15, 1], rotate: [0, 8, -8, 0] }} transition={{ duration: 2, repeat: Infinity }}
                    className="mx-auto w-16 h-16 rounded-full grid place-items-center"
                    style={{ background: "rgba(244,162,143,0.14)", border: `1px solid ${CORAL}77` }}
                >
                    <Heart size={30} weight="fill" color={CORAL} />
                </motion.div>
                <div className="font-serif text-2xl text-white mt-6">{t("travelDate.craftingOverlay.title")}</div>
                <div className="text-white/60 text-sm mt-2 min-h-[20px]">{craftingLines[i]}</div>
            </div>
        </motion.div>
    );
}


// ==================================================================
// Shared re-exports (used by TravelDateDetail)
// ==================================================================
export function DateCard({ r, onBuild, busy, idx }) {
    const { t } = useTranslation();
    return (
        <motion.article
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.04 }}
            className="rounded-3xl overflow-hidden relative"
            style={{ background: "var(--navy-deep)", border: `1px solid rgba(244,162,143,0.2)` }}
            data-testid={`td-result-${idx}`}
        >
            <div className="aspect-[4/5] bg-cover bg-center relative" style={{ backgroundImage: `url(${r.hero_image})` }}>
                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(11,19,32,0) 40%, rgba(5,10,17,0.95) 100%)" }} />
                <div className="absolute top-3 left-3"><TierBadge tier={r.tier} /></div>
                <div className="absolute top-3 right-3 rounded-full px-2.5 py-1 text-[10px] tracking-[0.2em] uppercase font-medium" style={{ background: "rgba(11,19,32,0.7)", color: CORAL, border: `1px solid ${CORAL}77`, backdropFilter: "blur(6px)" }}>
                    {t("travelDate.card.score", { score: r.score })}
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4">
                    <div className="font-serif text-2xl text-white leading-tight line-clamp-2">{r.title}</div>
                    <div className="text-white/70 text-xs mt-1 flex items-center gap-1">
                        <MapPin size={11} weight="fill" color={CORAL} />
                        <span>{r.location_name ? `${r.location_name} · ` : ""}{r.city}</span>
                    </div>
                </div>
            </div>
            <div className="p-4">
                <p className="text-white/80 text-[13px] leading-relaxed line-clamp-3">{r.why_recommended}</p>
                <div className="mt-3 flex items-center justify-between">
                    <div className="text-[10px] tracking-[0.2em] uppercase text-white/50">
                        {r.price_range} · ${r.est_cost_usd || "—"} · {r.duration_hours}h
                    </div>
                    <button onClick={onBuild} disabled={busy} className="text-xs px-3 py-1.5 rounded-full font-semibold inline-flex items-center gap-1 disabled:opacity-50" style={{ background: CORAL, color: "var(--navy)" }} data-testid={`td-build-${idx}`}>
                        {busy ? t("travelDate.card.building") : (<>{t("travelDate.card.planThis")} <ArrowRight size={10} weight="bold" /></>)}
                    </button>
                </div>
            </div>
        </motion.article>
    );
}

export function TierBadge({ tier, size = 12 }) {
    const filled = Math.max(1, Math.min(5, tier || 3));
    return (
        <div className="inline-flex items-center gap-0.5 rounded-full px-2.5 py-1" style={{ background: "rgba(11,19,32,0.75)", border: `1px solid ${CORAL}88`, backdropFilter: "blur(6px)" }}>
            {[1, 2, 3, 4, 5].map((n) => (
                <Heart key={n} size={size} weight={n <= filled ? "fill" : "duotone"} color={n <= filled ? CORAL : "rgba(244,162,143,0.35)"} />
            ))}
        </div>
    );
}
